import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Palette, Save } from 'lucide-react';
import { toast } from 'sonner';

interface WidgetConfig {
  id: string;
  organization_id: string;
  name: string;
  domain_whitelist: string[] | null;
  custom_css: Record<string, any> | null;
  branding_config: Record<string, any> | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface WidgetCustomizerProps {
  widget: WidgetConfig | null;
  onWidgetUpdate: (widget: WidgetConfig) => void;
  organizationId?: string;
}

export const WidgetCustomizer: React.FC<WidgetCustomizerProps> = ({
  widget,
  onWidgetUpdate,
  organizationId,
}) => {
  const [customizations, setCustomizations] = useState({
    primaryColor: widget?.branding_config?.primaryColor || '#3b82f6',
    secondaryColor: widget?.branding_config?.secondaryColor || '#64748b',
    fontFamily: widget?.branding_config?.fontFamily || 'Inter',
    logoUrl: widget?.branding_config?.logoUrl || '',
    showLogo: widget?.branding_config?.showLogo || true,
    borderRadius: widget?.branding_config?.borderRadius || '8',
    buttonStyle: widget?.branding_config?.buttonStyle || 'solid',
    customCSS: widget?.custom_css?.styles || '',
  });

  const [isSaving, setIsSaving] = useState(false);

  const handleCustomizationChange = (key: string, value: any) => {
    setCustomizations(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const saveCustomizations = async () => {
    if (!widget || !organizationId) {
      toast.error('Widget configuration not loaded');
      return;
    }

    setIsSaving(true);
    try {
      // In a real implementation, this would save to the database
      // For now, we'll just show a success message since the table might not exist yet
      toast.success('Widget customizations saved successfully');
      
      // Update the local widget state
      const updatedWidget = {
        ...widget,
        branding_config: {
          primaryColor: customizations.primaryColor,
          secondaryColor: customizations.secondaryColor,
          fontFamily: customizations.fontFamily,
          logoUrl: customizations.logoUrl,
          showLogo: customizations.showLogo,
          borderRadius: customizations.borderRadius,
          buttonStyle: customizations.buttonStyle,
        },
        custom_css: {
          styles: customizations.customCSS,
        },
      };
      
      onWidgetUpdate(updatedWidget);
    } catch (error) {
      console.error('Error saving customizations:', error);
      toast.error('Failed to save customizations');
    } finally {
      setIsSaving(false);
    }
  };

  if (!widget) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Widget Customization</CardTitle>
          <CardDescription>
            No widget configuration found. The hive360_widgets table may not exist yet.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-700">
              Widget customization will be available after the database migration is complete.
              For now, you can use the basic widget functionality with tokens.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Widget Appearance
          </CardTitle>
          <CardDescription>
            Customize the look and feel of your widget to match your brand
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Colors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="primaryColor">Primary Color</Label>
              <div className="flex gap-2">
                <Input
                  id="primaryColor"
                  type="color"
                  value={customizations.primaryColor}
                  onChange={(e) => handleCustomizationChange('primaryColor', e.target.value)}
                  className="w-16 h-10 p-1"
                />
                <Input
                  value={customizations.primaryColor}
                  onChange={(e) => handleCustomizationChange('primaryColor', e.target.value)}
                  placeholder="#3b82f6"
                  className="flex-1"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="secondaryColor">Secondary Color</Label>
              <div className="flex gap-2">
                <Input
                  id="secondaryColor"
                  type="color"
                  value={customizations.secondaryColor}
                  onChange={(e) => handleCustomizationChange('secondaryColor', e.target.value)}
                  className="w-16 h-10 p-1"
                />
                <Input
                  value={customizations.secondaryColor}
                  onChange={(e) => handleCustomizationChange('secondaryColor', e.target.value)}
                  placeholder="#64748b"
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          {/* Typography */}
          <div className="space-y-2">
            <Label htmlFor="fontFamily">Font Family</Label>
            <Select 
              value={customizations.fontFamily}
              onValueChange={(value) => handleCustomizationChange('fontFamily', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select font family" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Inter">Inter</SelectItem>
                <SelectItem value="Roboto">Roboto</SelectItem>
                <SelectItem value="Open Sans">Open Sans</SelectItem>
                <SelectItem value="Poppins">Poppins</SelectItem>
                <SelectItem value="Lato">Lato</SelectItem>
                <SelectItem value="system-ui">System Default</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Branding */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="showLogo">Show Logo</Label>
              <Switch
                id="showLogo"
                checked={customizations.showLogo}
                onCheckedChange={(checked) => handleCustomizationChange('showLogo', checked)}
              />
            </div>
            
            {customizations.showLogo && (
              <div className="space-y-2">
                <Label htmlFor="logoUrl">Logo URL</Label>
                <Input
                  id="logoUrl"
                  value={customizations.logoUrl}
                  onChange={(e) => handleCustomizationChange('logoUrl', e.target.value)}
                  placeholder="https://your-domain.com/logo.png"
                />
                <p className="text-sm text-muted-foreground">
                  Enter a direct URL to your logo image (PNG, JPG, or SVG recommended)
                </p>
              </div>
            )}
          </div>

          {/* Layout Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="borderRadius">Border Radius (px)</Label>
              <Input
                id="borderRadius"
                type="number"
                value={customizations.borderRadius}
                onChange={(e) => handleCustomizationChange('borderRadius', e.target.value)}
                min="0"
                max="20"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="buttonStyle">Button Style</Label>
              <Select 
                value={customizations.buttonStyle}
                onValueChange={(value) => handleCustomizationChange('buttonStyle', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select button style" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="solid">Solid</SelectItem>
                  <SelectItem value="outline">Outline</SelectItem>
                  <SelectItem value="ghost">Ghost</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advanced CSS */}
      <Card>
        <CardHeader>
          <CardTitle>Advanced Styling</CardTitle>
          <CardDescription>
            Add custom CSS to further customize your widget appearance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="customCSS">Custom CSS</Label>
            <Textarea
              id="customCSS"
              value={customizations.customCSS}
              onChange={(e) => handleCustomizationChange('customCSS', e.target.value)}
              placeholder=".hive360-widget { /* your custom styles */ }"
              className="h-32 font-mono text-sm"
            />
            <p className="text-sm text-muted-foreground">
              Add custom CSS rules to override default widget styling. Use the .hive360-widget class as the root selector.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={saveCustomizations} disabled={isSaving} className="min-w-32">
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};